import {
  ADMIN_ORDER_DETAILS,
  AIStarsIcon,
  Button,
  DetailRow,
  Div,
  H5,
  Icon,
  Img,
  Modal,
  PropsModal,
  Separator,
  StatusCircle,
  SubTitleWithTooltipIcon,
  TooltipIcon,
  VGrubsLogoSmall,
  VOrdersLogoSmall,
  bytesLogo,
  deliveryLogo,
  displayCentsInDollars,
  isAdmin,
  isPartner,
  kanekt365Logo,
  renderIncomeStatusName,
  vGrubsAILogo,
} from '@vgrubs/components';
import {
  IncomeStatus,
  NewTransactionResponse,
  vGrubsIncomeEntity,
  vOrdersIncomeType,
} from 'components/src/types/api/wallet.types';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  CashOrderTag,
  describeIncomeRow,
  showIncomeStatusColor,
  TransactionAmount,
} from '../TransactionComponents';
import { EarningExplanationModal } from './EarningExplanationModal';
import { IncomeBreakdown } from './IncomeBreakdown';

import { openInNewTab } from '../helpers';

export const IncomeDetailModal = ({
  modalIsOpen,
  closeModal,
}: PropsModal & {
  modalIsOpen: NewTransactionResponse<vGrubsIncomeEntity> | null;
}) => {
  const transaction = modalIsOpen?.object;

  const isBalanceCorrection =
    transaction?.type === vOrdersIncomeType.balanceIncrementation ||
    transaction?.type === vOrdersIncomeType.balanceDeduction;

  // A cash pickup order's amount is our fee taken from the balance, so it is
  // worded like a balance deduction, never as a payout.
  const isDeduction =
    transaction?.type === vOrdersIncomeType.balanceDeduction ||
    !!transaction?.cashPayment;

  const descriptionLabel = isBalanceCorrection
    ? 'Balance Correction'
    : 'New Income';

  const { sign, amountColor } = describeIncomeRow(transaction ?? undefined);

  const paymentReportIncome = transaction?.paymentReportIncome;

  const [aiModalOpen, setAiModalOpen] = useState(false);
  // Per the endpoint's contract: only report and vGrubs/3PD orders can be
  // explained. A nothingReceived report still qualifies - the backend answers
  // those with a message worth showing - but a vGrubs-own channel answers 400
  // ("This order has no payout breakdown to explain"), so the type has to be
  // checked as well: since Aug 2026 those orders carry `paymentReportIncome`
  // too, and having a breakdown no longer tells the two paths apart.
  const isVGrubsOwnChannel =
    transaction?.type === vOrdersIncomeType.vOrders ||
    transaction?.type === vOrdersIncomeType.vGrubsAI ||
    transaction?.type === vOrdersIncomeType.bytes ||
    transaction?.type === vOrdersIncomeType.kanekt365;
  const showAiSummary =
    transaction?.orderId != null &&
    !!paymentReportIncome &&
    !isVGrubsOwnChannel;

  // This component stays mounted after the income modal closes (callers just
  // null the row), so the AI modal's open flag must not survive into the next
  // row's modal.
  useEffect(() => {
    if (!modalIsOpen) setAiModalOpen(false);
  }, [modalIsOpen]);

  // Every payout path now arrives as one list of lines - vOrders and the other
  // vGrubs-own channels included - so there is nothing left to lay out by hand.
  // A nothingReceived report still lists its components: they add up to $0 and
  // are exactly what explains the $0, so they render like any other.
  const breakdownLines = paymentReportIncome?.components ?? [];
  const showIncomeBreakdown = breakdownLines.length > 0;

  return (
    <>
      <Modal
        isOpen={!!modalIsOpen}
        closeModal={closeModal}
        logo={false}
        textAlign={'left'}
        header={'Income Details'}
        zIndex={10}
      >
        <DetailRow label="Date & Time">
          {DateTime.fromISO(transaction?.createdAt || '').toFormat(
            'MMM dd, yyyy - hh:mm a'
          )}
        </DetailRow>
        {transaction?.orderId && (
          <>
            <Separator $m="16px 0" />
            <DetailRow
              label="Order ID"
              onOpen={() =>
                openInNewTab(
                  `${ADMIN_ORDER_DETAILS.replace(
                    ':id',
                    transaction.orderId?.toString() || ''
                  )}`
                )
              }
            >
              {`#${transaction.orderId}`}
            </DetailRow>
          </>
        )}

        {transaction?.storeName && (
          <>
            <Separator $m="16px 0" />
            <DetailRow label="Store Name">{transaction.storeName}</DetailRow>
          </>
        )}

        <Separator $m="16px 0" />
        <DetailRow
          label="Description"
          right={
            <Icon>
              {transaction?.type === vOrdersIncomeType.vGrubs && (
                <VGrubsLogoSmall />
              )}
              {transaction?.type === vOrdersIncomeType.vOrders && (
                <VOrdersLogoSmall />
              )}
              {transaction?.type === vOrdersIncomeType.vGrubsAI && (
                <Img alt="" src={vGrubsAILogo} $height="40px" />
              )}
              {transaction?.type === vOrdersIncomeType.bytes && (
                <Img
                  alt=""
                  $height="40px"
                  src={isAdmin ? bytesLogo : vGrubsAILogo}
                />
              )}
              {transaction?.type === vOrdersIncomeType.kanekt365 && (
                <Img
                  alt=""
                  $height="40px"
                  src={isAdmin ? kanekt365Logo : vGrubsAILogo}
                />
              )}
              {transaction?.type === vOrdersIncomeType.dcom && (
                <Img alt="" $height="40px" src={deliveryLogo} />
              )}
            </Icon>
          }
        >
          {transaction?.cashPayment ? (
            <Div $flex $alignCenter $gap="8px" $wrap>
              <H5 $m="0">{descriptionLabel}</H5>
              <CashOrderTag />
            </Div>
          ) : (
            descriptionLabel
          )}
        </DetailRow>

        {isBalanceCorrection && transaction?.reason && (
          <>
            <Separator $m="16px 0" />
            <DetailRow label="Reason">
              {/* The backend's own text, one line per `\n` (a debt repayment
                  lists owed / taken / left on lines of their own). */}
              <H5 $m="0" style={{ whiteSpace: 'pre-line' }}>
                {transaction.reason}
              </H5>
            </DetailRow>
          </>
        )}

        <Separator $m="16px 0" />
        <DetailRow label="Status">
          <Div $flex $alignCenter $gap="8px">
            <StatusCircle
              $color={showIncomeStatusColor(transaction?.status || undefined)}
            />
            <H5 $m="0" $capitalize>
              {renderIncomeStatusName(transaction?.status ?? '')}
            </H5>
            {transaction?.status !== IncomeStatus.available && (
              <TooltipIcon
                tooltip={
                  transaction?.availableAt
                    ? !isDeduction
                      ? `Will be paid out to ${
                          isAdmin ? 'merchants' : 'your'
                        } vPay Card on ${DateTime.fromISO(
                          transaction.availableAt
                        ).toFormat('MMM dd, yyyy')}.`
                      : `Will be deducted from ${
                          isAdmin ? 'merchants' : 'your'
                        } vPay Card on ${DateTime.fromISO(
                          transaction.availableAt
                        ).toFormat('MMM dd, yyyy')}.`
                    : ''
                }
              />
            )}
          </Div>
        </DetailRow>

        {showIncomeBreakdown && (
          <>
            <Separator $m="16px 0" />
            <H5 $mb="8">Income Breakdown</H5>
            <IncomeBreakdown lines={breakdownLines} />
          </>
        )}

        {showAiSummary && (
          <Button
            $blueGradient
            $selected
            $w="100%"
            $mt="16"
            onClick={() => setAiModalOpen(true)}
          >
            <Div $flex $center $gap="8px">
              <Icon>
                <AIStarsIcon />
              </Icon>
              Summarize With AI
            </Div>
          </Button>
        )}

        <Separator $m="16px 0" />
        <Div $flex $alignCenter $between>
          {transaction?.deductionAmount ? (
            <SubTitleWithTooltipIcon
              text="Total Amount"
              tooltip={`Customer changed the order.
                 Order total was reduced by ${displayCentsInDollars(
                   transaction?.deductionAmount || 0
                 )}.
                 ${
                   isPartner ? 'Your payout' : 'Payout'
                 }  is now ${displayCentsInDollars(
                transaction?.amount || 0
              )} instead of ${displayCentsInDollars(
                transaction?.grossAmount || 0
              )}.`}
            />
          ) : (
            <H5 $m="0">Total Amount</H5>
          )}
          <TransactionAmount
            amount={transaction?.amount}
            sign={sign}
            color={amountColor}
          />
        </Div>
      </Modal>

      <EarningExplanationModal
        isOpen={aiModalOpen && showAiSummary}
        closeModal={() => setAiModalOpen(false)}
        orderId={transaction?.orderId}
        components={paymentReportIncome?.components ?? []}
      />
    </>
  );
};
